// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, StyleSheet, View } from 'react-native';
import { CameraView, useCameraPermissions, type BarcodeScanningResult } from 'expo-camera';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

type InviteQrScannerModalProps = {
  visible: boolean;
  onClose: () => void;
  onScanned: (value: string) => void;
};

/**
 * Full-screen camera for reading an invite QR code.
 *
 * The camera only mounts while the modal is open — an unmounted `CameraView` releases the
 * device, which matters on the start screen because it is the first thing a signed-out user
 * sees and a camera left running there would keep the indicator lit for the whole session.
 *
 * On web this needs a secure origin: `getUserMedia` is unavailable over plain http, so the
 * permission request resolves straight to denied on anything but https or localhost.
 */
export function InviteQrScannerModal({ visible, onClose, onScanned }: InviteQrScannerModalProps) {
  const { t } = useI18n();
  const [permission, requestPermission] = useCameraPermissions();
  const [requesting, setRequesting] = useState(false);
  /*
   * A QR code in frame fires `onBarcodeScanned` many times a second. Without this latch the
   * first scan would navigate and every later frame would fire again on top of it.
   */
  const handledRef = useRef(false);

  useEffect(() => {
    if (visible) {
      handledRef.current = false;
    }
  }, [visible]);

  const ensurePermission = useCallback(async () => {
    if (permission?.granted) {
      return;
    }
    setRequesting(true);
    try {
      await requestPermission();
    } finally {
      setRequesting(false);
    }
  }, [permission?.granted, requestPermission]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    // Undetermined only: a denied permission must not be re-prompted on every open.
    if (permission && !permission.granted && permission.canAskAgain && permission.status === 'undetermined') {
      void ensurePermission();
    }
  }, [visible, permission, ensurePermission]);

  const handleBarcodeScanned = useCallback(
    ({ data }: BarcodeScanningResult) => {
      if (handledRef.current) {
        return;
      }
      const value = String(data ?? '').trim();
      if (!value) {
        return;
      }
      handledRef.current = true;
      onScanned(value);
    },
    [onScanned],
  );

  const granted = permission?.granted === true;
  const denied = permission != null && !permission.granted && permission.status !== 'undetermined';

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
      presentationStyle={Platform.OS === 'web' ? undefined : 'fullScreen'}
    >
      <View style={styles.container}>
        <Text style={styles.title}>{t('start.qrScanTitle')}</Text>
        <Text style={styles.subtitle}>{t('start.qrScanSubtitle')}</Text>

        <View style={styles.viewport}>
          {granted && visible ? (
            <CameraView
              style={StyleSheet.absoluteFill}
              facing="back"
              barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
              onBarcodeScanned={handleBarcodeScanned}
              testID="invite-qr-camera"
            />
          ) : (
            <View style={styles.placeholder}>
              {requesting || !permission ? (
                <ActivityIndicator size="large" color={appColors.accent.primary} />
              ) : (
                <Text style={styles.placeholderText}>
                  {denied ? t('start.qrScanPermissionDenied') : t('start.qrScanPermissionPrompt')}
                </Text>
              )}
            </View>
          )}
          <View pointerEvents="none" style={styles.reticle} />
        </View>

        {denied ? (
          <GoffyButton
            label={t('start.qrScanGrantCta')}
            onPress={() => {
              void ensurePermission();
            }}
            disabled={requesting || permission?.canAskAgain === false}
            tone="accent"
            style={styles.button}
          />
        ) : null}
        {denied && permission?.canAskAgain === false ? (
          <Text style={styles.hint}>{t('start.qrScanPermissionBlocked')}</Text>
        ) : null}

        <GoffyButton
          label={t('start.qrScanCancelCta')}
          onPress={onClose}
          tone="dark"
          style={styles.button}
          testID="invite-qr-cancel"
        />
      </View>
    </Modal>
  );
}

export default InviteQrScannerModal;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    gap: 12,
    justifyContent: 'center',
    backgroundColor: '#011806',
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    color: '#A1B49E',
    maxWidth: 420,
    alignSelf: 'center',
  },
  viewport: {
    width: '100%',
    maxWidth: 460,
    aspectRatio: 1,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    overflow: 'hidden',
  },
  placeholder: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  placeholderText: {
    color: '#A1B49E',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
  reticle: {
    position: 'absolute',
    top: '15%',
    left: '15%',
    right: '15%',
    bottom: '15%',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.55)',
    borderRadius: 12,
  },
  hint: {
    color: '#A1B49E',
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    maxWidth: 420,
    alignSelf: 'center',
  },
  button: {
    width: '100%',
    maxWidth: 460,
    alignSelf: 'center',
  },
});
